import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { Logo } from '../components/Logo';
import { Lock, Clock, Calendar, MapPin } from 'lucide-react';
import { focalPosition } from '../lib/photoFocal';

export default function VaultAccess() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/vault/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(formatApiError(e, 'Access denied.')));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--primary))] text-[#FDFBF7] p-8 text-center">
        <div>
          <Logo tone="cream" size={56} />
          <Lock className="w-10 h-10 mx-auto mt-6 text-[hsl(var(--accent))]" />
          <h1 className="font-serif text-4xl mt-4">This door is closed</h1>
          <p className="mt-3 text-[#FDFBF7]/70 max-w-md mx-auto">{err}</p>
          <Link to="/" className="ml-btn-outline !text-[#FDFBF7] !border-[#FDFBF7]/50 mt-6 inline-flex">Return home</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[hsl(var(--primary))] grid place-items-center text-[#FDFBF7] font-serif text-xl">Opening Family Vault…</div>;

  const { memorial, media, family, life_events, granted_to, expires_at, access_token } = data;
  const hoursLeft = Math.max(0, Math.round((new Date(expires_at) - new Date()) / 3600000));

  return (
    <div className="min-h-screen bg-background">
      <div className="ml-container py-5 flex justify-between items-center">
        <Link to="/"><Logo size={32} /></Link>
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <Clock className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
          Access expires in ~{hoursLeft}h
        </div>
      </div>

      <section className="bg-[hsl(var(--primary))] text-[#FDFBF7]">
        <div className="ml-container py-12 text-center">
          <div className="ml-overline !text-[#C5A059]">Welcome, {granted_to}</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">The Family Vault of {memorial.full_name}</h1>
          <p className="mt-3 text-[#FDFBF7]/75 max-w-xl mx-auto">
            This is a private area shared with family and close friends. Please treat every photograph and story with care.
          </p>
        </div>
      </section>

      <section className="ml-container py-12">
        <div className="ml-overline">Vault memories</div>
        <h2 className="font-serif text-3xl mt-2 text-[hsl(var(--primary))]">Photos & videos</h2>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="vault-media-grid">
          {media.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No media yet.</div>}
          {media.map((m) => {
            const url = m.external_url || `${process.env.REACT_APP_BACKEND_URL}/api/media/${m.media_id}/file?token=${access_token}`;
            return (
              <div key={m.media_id} className="relative aspect-square rounded-xl overflow-hidden bg-[hsl(var(--muted))]">
                <img src={url} alt={m.caption || ''} className="w-full h-full object-cover" style={{ objectPosition: focalPosition(m) }} />
                {m.caption && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[hsl(var(--primary))]/85 to-transparent text-[#FDFBF7] p-2 text-xs">{m.caption}</div>}
                {m.visibility === 'vault' && <div className="absolute top-2 right-2 ml-overline !text-[hsl(var(--accent))] bg-[hsl(var(--primary))]/80 px-2 py-0.5 rounded">Vault</div>}
              </div>
            );
          })}
        </div>
      </section>

      <section className="ml-container py-12 border-t border-border">
        <div className="ml-overline">Family tree</div>
        <h2 className="font-serif text-3xl mt-2 text-[hsl(var(--primary))]">Those who walked beside</h2>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {family.map((f) => (
            <div key={f.family_id} className="ml-card !p-4 text-center">
              <span className={`inline-block w-3 h-3 rounded-full ${f.is_living ? 'bg-[hsl(var(--secondary))]' : 'bg-[hsl(var(--accent))]'}`} />
              <div className="font-serif text-lg mt-2 text-[hsl(var(--primary))]">{f.name}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{f.relationship}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="ml-container py-12 border-t border-border">
        <div className="ml-overline">Life journey</div>
        <h2 className="font-serif text-3xl mt-2 text-[hsl(var(--primary))]">Milestones</h2>
        <div className="mt-6 space-y-3">
          {life_events.map((e) => (
            <div key={e.event_id} className="ml-card">
              <div className="ml-overline !text-[hsl(var(--secondary))]">{e.category}</div>
              <div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">{e.title}</div>
              {e.description && <p className="text-sm mt-2 text-[hsl(var(--muted-foreground))] leading-relaxed">{e.description}</p>}
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-3 flex flex-wrap gap-3">
                {e.start_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {e.start_date}{e.end_date ? ` — ${e.end_date}` : ''}</span>}
                {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="ml-container py-8 text-center text-xs text-[hsl(var(--muted-foreground))] border-t border-border">
        This secure link was opened for <strong>{granted_to}</strong> and may be revoked by the family at any time.
      </footer>
    </div>
  );
}
